import { useContext, useEffect, useRef } from "react";
import { FormattedMessage, IntlProvider } from "react-intl";
import homeMessages from "../translate/homeMessages";
import { LangContext, iconSize } from "../App";
import Logo600 from "../images/logo600.png";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";
import { Helmet } from "react-helmet-async";
import Me from "../images/DSC_1652.JPG";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Link as MuiLink,
} from "@mui/material";

const techTags = [
  "React.js",
  "PHP",
  "Node.js",
  "TypeScript",
  "REST API",
  "Cloud",
  "Bootstrap",
  "Material UI",
  "MySQL",
  "Postfix",
  "MSSQL",
  "VPS",
  "DNS",
  "SEO",
  "Docker",
  "GIT",
  "CircleCI",
];

const styles = {
  wrapper: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    padding: "80px 24px",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
  },
  headerZone: {
    textAlign: "center",
    marginBottom: "50px",
  },
  headerP: {
    fontSize: "18px",
    color: "#4b5563",
  },
  mainTitle: {
    fontSize: "36px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.02em",
    margin: "0 0 16px 0",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "32px",
    alignItems: "start",
    width: "100%",
  },
  card: {
    background: "#ffffff",
    borderRadius: "24px",
    padding: "36px",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.04)",
    border: "1px solid #e2e8f0",
    height: "100%",
    boxSizing: "border-box",
  },
  cardHeaderGradient: {
    background: "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
    padding: "10px",
    borderRadius: "22px",
    textAlign: "left",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cardTitleGradient: {
    color: "#fff",
    margin: 0,
    fontSize: "20px",
  },
  imageContainer: {
    gridColumn: "1 / -1",
    textAlign: "center",
    marginTop: "50px",
  },
  imgStyle: {
    maxWidth: "100%",
    width: "400px",
    height: "auto",
    borderRadius: "20px",
    boxShadow: "0 20px 40px -20px rgba(0,0,0,0.1)",
  },
};

const Home = () => {
  const language = useContext(LangContext);
  const imgRef = useRef(null);

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => {
        return fetch(
          "https://michaljaskulski.pl/api/address.php?ipAddress=" + data.ip,
        );
      })
      .catch((error) => console.error("Błąd:", error));
  }, []);

  const renderFeatureCards = () => {
    if (language === "pl") {
      return (
        <>
          <FeatureCard title="React Frontend">
            Nowoczesne aplikacje SPA, panele administracyjne i systemy
            biznesowe.
          </FeatureCard>
          <FeatureCard title="Backend & API">
            PHP, REST API, integracje z systemami zewnętrznymi.
          </FeatureCard>
          <FeatureCard title="Bazy danych">
            MySQL, MSSQL, optymalizacja wydajności.
          </FeatureCard>
          <FeatureCard title="VPS & DevOps">
            Docker, Linux, SSL, kopie bezpieczeństwa.
          </FeatureCard>
          <FeatureCard title="Projektowanie i architektura">
            Struktura aplikacji, dobór technologii, skalowalność i czytelny kod.
          </FeatureCard>
          <FeatureCard title="Bezpieczeństwo aplikacji">
            Walidacja danych, bezpieczne API, uprawnienia użytkowników i ochrona
            danych.
          </FeatureCard>
          <FeatureCard title="Wdrożenia i utrzymanie">
            Publikacja aplikacji, konfiguracja środowiska produkcyjnego i
            monitoring działania.
          </FeatureCard>
          <FeatureCard title="IT Management">
            Organizacja pracy, zarządzanie infrastrukturą IT i kompleksowa
            realizacja projektów.
          </FeatureCard>
        </>
      );
    }

    return (
      <>
        <FeatureCard title="Comprehensive IT solutions">
          From initial concept to production release. I build software products
          that truly empower businesses and adapt to their long-term growth.
        </FeatureCard>
        <FeatureCard title="Tailored Web Applications">
          Designing and implementing modern web solutions tailored precisely to
          your internal workflows – swift, seamless, and scalable.
        </FeatureCard>
        <FeatureCard title="Cloud Systems">
          Deploying custom cloud architectures that guarantee robust uptime,
          industry-standard security practices, and flexible cost efficiency.
        </FeatureCard>
        <FeatureCard title="Secure Backend Architectures">
          Engineering fast, reliable backend microservices and RESTful APIs,
          securing entry points, and focusing strictly on data optimization.
        </FeatureCard>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Fullstack React & PHP Developer | Michał Jaskulski</title>
        <meta
          name="description"
          content="Tworzę nowoczesne aplikacje webowe SPA (React, PHP, MSSQL, MySQL, Linux, VPS). Dedykowane systemy dla firm, integracje REST API oraz obsługa serwerów VPS."
        />
        <meta
          property="og:title"
          content="Fullstack React & PHP Developer | Michał Jaskulski"
        />
        <meta
          property="og:description"
          content="Projektowanie i wdrażanie skalowalnych aplikacji internetowych oraz systemów biznesowych."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://michaljaskulski.pl" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Allworks.pl - Michał Jaskulski",
            legalName: "Allworks.pl Michał Jaskulski",
            url: "https://michaljaskulski.pl",
            logo: "https://michaljaskulski.pl/logo600.png",
            description:
              "Tworzenie dedykowanych aplikacji webowych w React, PHP i usługi programistyczne oraz wsparcie IT dla firm.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "ul. Brzozowa 14D",
              addressLocality: "Mikołów",
              postalCode: "43-190",
              addressCountry: "PL",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: "50.1678",
              longitude: "18.9056",
            },
            vatID: "PL6351848380",
            telephone: "+48602209858",
            email: "michal.jaskulski@allworks.pl",
            priceRange: "$$",
          })}
        </script>
      </Helmet>

      <Box
        className="bodyBackground"
        sx={{
          backgroundColor: "#f8fafc",
          minHeight: "100vh",
          px: 3,
          pt: 10,
          pb: 5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <IntlProvider locale={language} messages={homeMessages[language]}>
          <AnimatePresence>
            <Container
              component={motion.div}
              maxWidth="lg"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
              disableGutters
            >
              <Box
                sx={{
                  width: "100%",
                  textAlign: "left",
                  mb: 1,
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "inherit",
                    fontSize: {
                      xs: "clamp(12px, 4.8vw, 28px)",
                      sm: "36px",
                      md: "42px",
                    },
                    fontWeight: 700,
                    color: "#0f172a",
                    letterSpacing: { xs: "-0.045em", sm: "-0.03em" },
                    lineHeight: 1.2,
                    textAlign: "center",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    m: 0,
                    whiteSpace: "wrap",
                    overflow: "hidden",
                  }}
                >
                  <FormattedMessage id="fullstack" />
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "inherit",
                    fontSize: {
                      xs: "clamp(12px, 4.8vw, 28px)",
                      sm: "36px",
                      md: "42px",
                    },
                    fontWeight: 700,
                    color: "#4169E1",
                    letterSpacing: { xs: "-0.045em", sm: "-0.03em" },
                    lineHeight: 1.2,
                    textAlign: "center",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    m: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  <FormattedMessage id="fullstackMJ" />
                </Typography>
              </Box>

              <Grid container spacing={6} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      position: { xs: "static", md: "sticky" },
                      top: 40,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 3,
                      bgcolor: "#ffffff",
                      borderRadius: 6,
                      p: 5,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.03)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "90%",
                        aspectRatio: "1/1",
                        overflow: "hidden",
                        boxShadow: "0 20px 40px -15px rgba(79, 70, 229, 0.2)",
                        border: "4px solid #ffffff",
                        outline: "1px solid #e2e8f0",
                      }}
                    >
                      <Box
                        component="img"
                        ref={imgRef}
                        src={Me}
                        alt="Michał Jaskulski - Fullstack React Developer"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                          mt: 2,
                        }}
                      >
                        <StatCard number="20+" label="lat w IT" />
                        <StatCard number="40+" label="projektów" />
                        <StatCard
                          number="Fullstack"
                          label="frontend i backend"
                        />
                        <StatCard
                          number="React"
                          label="nowoczesne aplikacje webowe"
                        />
                        <StatCard
                          number="End-to-End"
                          label="od projektu do wdrożenia"
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "center",
                        mt: 1.5,
                      }}
                    >
                      {techTags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            borderRadius: "8px",
                            fontWeight: 600,
                            fontSize: "13px",
                          }}
                        />
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      href="/kontakt"
                      sx={{
                        backgroundColor: "#0f172a",
                        color: "#ffffff",
                        textTransform: "none",
                        px: 3,
                        py: 1.5,
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: 600,
                        mt: 2,
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
                        "&:hover": {
                          backgroundColor: "#1e293b",
                        },
                      }}
                    >
                      {language === "pl"
                        ? "Porozmawiajmy o projekcie"
                        : "Let's talk about your project"}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 3,
                    }}
                  >
                    {renderFeatureCards()}
                  </Box>
                </Grid>
              </Grid>

              {/* Sekcja Wybranych Realizacji (Case Studies) - KROK 3 SEO */}
              <Box sx={{ mt: 8, pt: 4, borderTop: "1px solid #e2e8f0" }}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: { xs: "24px", sm: "30px" },
                    fontWeight: 800,
                    color: "#0f172a",
                    mb: 4,
                    textAlign: "center",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {language === "pl"
                    ? "Wybrane realizacje i wdrożenia komercyjne"
                    : "Featured Commercial Projects"}
                </Typography>

                <Grid container spacing={4}>
                  {/* Case Study 1: PZITB Rzeczoznawcy */}
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: "20px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 4px 20px -2px rgba(0,0,0,0.02)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h5"
                          component="h4"
                          sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            color: "#4f46e5",
                            fontSize: "20px",
                          }}
                        >
                          PZITB Rzeczoznawcy — Portal dla Wykonawców
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#475569",
                            lineHeight: 1.6,
                            mb: 3,
                            fontSize: "15px",
                          }}
                        >
                          {language === "pl"
                            ? "Dedykowany portal dla Polskiego Związku Inżynierów i Techników Budownictwa (pzitbrz.pl). System automatyzuje i usprawnia koordynację zleceń dla wykonawców, opierając się na wydajnej architekturze backendowej PHP oraz nowoczesnym interfejsie użytkownika."
                            : "Dedicated portal for the Polish Association of Building Engineers and Technicians. Streamlines and automates workflow management for contractor assignments using PHP Slim API and responsive UI."}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                          label="React.js"
                          size="small"
                          sx={{
                            bgcolor: "#eef2ff",
                            color: "#4f46e5",
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          label="PHP Slim API"
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          label="Bazy danych"
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>

                  {/* Case Study 2: Twoja Opiekunka */}
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: "20px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 4px 20px -2px rgba(0,0,0,0.02)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h5"
                          component="h4"
                          sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            color: "#4f46e5",
                            fontSize: "20px",
                          }}
                        >
                          Twoja Opiekunka — Portal Usługowy
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#475569",
                            lineHeight: 1.6,
                            mb: 3,
                            fontSize: "15px",
                          }}
                        >
                          {language === "pl"
                            ? "Kompleksowa budowa portalu łączącego klientów ze zweryfikowanymi opiekunami seniorów (twojaopiekunka.pl). Implementacja obejmowała weryfikację SMS, zaawansowaną wyszukiwarkę z selektorem miast, wgrywanie zdjęć oraz reset haseł."
                            : "End-to-end web portal connecting clients with verified senior caregivers. Built with SMS verification API, city selector, image uploads, and secure password reset workflows."}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                          label="Fullstack React"
                          size="small"
                          sx={{
                            bgcolor: "#eef2ff",
                            color: "#4f46e5",
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          label="SMS API & Auth"
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          label="REST API"
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </AnimatePresence>
        </IntlProvider>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2.5,
            flexWrap: "wrap",
            mt: 5,
          }}
        >
          {Logo600 && (
            <Box
              component="img"
              src={Logo600}
              alt="Logo firmy"
              sx={{
                maxWidth: "180px",
                height: "auto",
                opacity: 0.8,
              }}
            />
          )}
          <MuiLink
            href="/fullstack-developer-slask"
            underline="none"
            sx={{
              color: "#64748b",
              fontSize: "13px",
              fontWeight: 500,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#334155",
              },
            }}
          >
            Fullstack Developer - województwo śląskie
          </MuiLink>
        </Box>
      </Box>
    </>
  );
};

const FeatureCard = ({ title, children }) => (
  <Card
    elevation={0}
    sx={{
      background: "#ffffff",
      borderRadius: "16px",
      p: 1,
      boxShadow: "0 4px 20px -2px rgba(0,0,0,0.02)",
      border: "1px solid #e2e8f0",
    }}
  >
    <CardContent sx={{ p: "16px !important" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          mb: 1,
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ ...iconSize, color: "#c9a66b" }} />
        <Typography
          variant="h6"
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: "15px",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        {children}
      </Typography>
    </CardContent>
  </Card>
);

const StatCard = ({ number, label }) => (
  <Box
    sx={{
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      py: 1.25,
      px: 2,
      textAlign: "center",
      minWidth: "90px",
    }}
  >
    <Typography
      sx={{
        fontSize: "24px",
        fontWeight: 800,
        color: "#4f46e5",
        lineHeight: 1.2,
      }}
    >
      {number}
    </Typography>
    <Typography
      sx={{
        fontSize: "12px",
        color: "#64748b",
      }}
    >
      {label}
    </Typography>
  </Box>
);

export default Home;
